import { Plus } from 'lucide-react';
import {
  type CreateTestParameterInput,
  type TestParameter,
  createTestParameterSchema,
} from '#shared/types';
import { Banner } from '#client/components/ui/Banner';
import { Button } from '#client/components/ui/Button';
import { Card } from '#client/components/ui/Card';
import { FormActions } from '#client/components/ui/FormActions';
import { ResourceFormShell } from '#client/components/ui/ResourceFormShell';
import { Select } from '#client/components/ui/Select';
import { TextField } from '#client/components/ui/TextField';
import { useFormValues, type SetField } from '#client/hooks/useFormValues';
import { useRepeater } from '#client/hooks/useRepeater';
import { useResourceForm } from '#client/hooks/useResourceForm';
import { RESOURCE } from '#client/lib/routes';
import { ParameterRangeFields } from './ParameterRangeFields';
import { ParameterResultFields } from './ParameterResultFields';
import {
  EMPTY_RANGE,
  EMPTY_RESULT,
  FORMULA_OPTIONS,
  type ParameterIdentity,
  type RangeRow,
  type ResultRow,
  formulaHelp,
  initialIdentity,
  initialRanges,
  initialResults,
} from './parameter-form.logic';

const PATH = RESOURCE.testParameters;

export function TestParameterFormPage() {
  return (
    <ResourceFormShell<TestParameter> path={PATH} noun="parameter">
      {({ id, record }) => <ParameterForm id={id} record={record} />}
    </ResourceFormShell>
  );
}

function ParameterForm({
  id,
  record,
}: {
  id?: string | undefined;
  record?: TestParameter | undefined;
}) {
  const { values, set } = useFormValues(initialIdentity(record));

  // The two lists are separate repeaters rather than fields of `values`: each
  // is appended to and removed from independently, and the payload is composed
  // at submit.
  const ranges = useRepeater<RangeRow>(initialRanges(record), EMPTY_RANGE);
  const results = useRepeater<ResultRow>(initialResults(record), EMPTY_RESULT);

  const form = useResourceForm({
    path: PATH,
    id,
    schema: createTestParameterSchema,
    noun: 'Parameter',
  });

  const payload: CreateTestParameterInput = {
    ...values,
    values: ranges.rows,
    // A calculated result is never picked from a list; the schema refuses both.
    results: values.formula === null ? results.rows : [],
  };

  return (
    <form onSubmit={form.handleSubmit(payload)} noValidate>
      <div className="c-form-stack">
        {form.formError && <Banner tone="critical">{form.formError}</Banner>}

        <IdentityCard values={values} onFieldChange={set} errors={form.errors} />

        <Card
          title="Normal ranges"
          actions={
            <Button size="sm" icon={<Plus />} onClick={ranges.add}>
              Add range
            </Button>
          }
        >
          {form.errors.values && <Banner tone="critical">{form.errors.values}</Banner>}
          <ParameterRangeFields ranges={ranges} errors={form.errors} />
        </Card>

        {values.formula === null && (
          <Card
            title="Selectable results"
            actions={
              <Button size="sm" icon={<Plus />} onClick={results.add}>
                Add result
              </Button>
            }
          >
            <ParameterResultFields results={results} errors={form.errors} />
          </Card>
        )}

        <FormActions
          cancelTo={PATH}
          submitLabel={form.isEdit ? 'Save changes' : 'Create parameter'}
          submitting={form.submitting}
        />
      </div>
    </form>
  );
}

interface IdentityCardProps {
  values: ParameterIdentity;
  onFieldChange: SetField<ParameterIdentity>;
  errors: Record<string, string>;
}

/** Name, code, and whether the result is calculated rather than typed. */
function IdentityCard({ values, onFieldChange, errors }: IdentityCardProps) {
  return (
    <Card title="Parameter">
      <div className="c-form-grid">
        <TextField
          label="Name"
          value={values.name}
          error={errors.name}
          onChange={(event) => onFieldChange('name', event.target.value)}
          required
        />
        <TextField
          label="Code"
          value={values.code}
          error={errors.code}
          onChange={(event) => onFieldChange('code', event.target.value)}
          required
        />
        <Select
          label="Calculated by formula"
          placeholder="Not calculated — typed by the technician"
          options={FORMULA_OPTIONS}
          value={values.formula ?? ''}
          error={errors.formula}
          helpText={formulaHelp(values.formula)}
          onChange={(formula) => onFieldChange('formula', formula === '' ? null : formula)}
        />
      </div>
    </Card>
  );
}
